/**
 * War in diesem Mikrofon-Strom ueberhaupt ein Signal? (22.09.2026, #2494)
 *
 * ## Woher das kommt
 *
 * Martin: *„Beim Klick auf Push-to-Talk geht die Aufnahme kurz an und sofort
 * wieder weg. Er muss ein zweites, manchmal drittes Mal druecken."* Er
 * vermutete eine Zeit- oder Schwellenwert-Erfassung beim Klick.
 *
 * Am Protokoll nachgemessen — und es war etwas anderes:
 *
 *     17:56:32  ptt-t  path=cold  kalt=tts-start-hifi  rec=+382ms
 *     17:56:32  ptt-stream  4278 Bytes, -72.4 dBFS  → verworfen
 *     17:56:34  ptt-t  path=warm  rec=+29ms
 *     17:56:35  ptt-stream  4293 Bytes, -73.1 dBFS  → verworfen
 *     17:56:36  ptt-t  path=warm  rec=+32ms
 *
 * Der Knopf hat jedes Mal gegriffen, und zwar schnell. Das Mikrofon hat
 * nichts geliefert: -72 dBFS ist keine leise Stimme und kein leiser Raum.
 * In der eigenen Messreihe des Servers (server/stille-pegel.ts) liegt die
 * LEISESTE echte Zimmeraufnahme bei -59,2 dBFS. Das hier ist ein toter
 * Strom.
 *
 * ## ⚠️ Warum es beim zweiten und dritten Druck genauso blieb
 *
 * Der erste Griff ans Mikro geschah kurz nach der Wiedergabe
 * (`kalt=tts-start-hifi`). Dieser Strom BLEIBT danach warm — und die
 * Wiederverwendung prueft nur zwei Dinge: das richtige Geraet und
 * `readyState === "live"`. Ein stiller Strom ist beides. Er wurde also bei
 * jedem weiteren Druck erneut ausgegeben.
 *
 * Der Kommentar neben der Geraete-Pruefung sagt denselben Satz schon fuer
 * den anderen Fall: *„sonst konserviert das Warmhalten das stille Mikro"*.
 * Dieselbe Falle, ein zweiter Weg hinein.
 *
 * ## Die Regel hier
 *
 * Eine Aufnahme, in der die Spitze ueber die ganze Dauer NULL war, kann
 * keinen Ton enthalten haben. Dann ist nicht die Aufnahme schuld, sondern
 * die Leitung — und die gehoert weggeworfen, nicht aufgehoben.
 *
 * ⚠️ Bewusst die SPITZE und nicht der Mittelwert: ein einzelnes Rascheln
 *    genuegt als Beweis, dass die Leitung traegt. Und bewusst exakt Null,
 *    nicht „sehr leise" — ein leiser Raum ist ein Fall fuer die
 *    Stille-Pruefung am Server, nicht fuer das Wegwerfen der Leitung.
 *
 * ⚠️ Eine zu kurze Aufnahme beweist gar nichts: wer antippt und sofort
 *    loslaesst, bekommt Null, ohne dass die Leitung kaputt ist.
 *
 * ## ⚠️⚠️ 24.09.2026, Ticket 2518 — DIE WACHE ALLEIN IST KEIN BEWEIS
 *
 * Nachgezählt an allen Sprechtasten-Drücken vom 22. bis 24.09.:
 *
 *     warm  53 Drücke   9 leere Aufnahmen (zu-klein bytes=5)
 *                       8× ptt-stiller-strom — alle 8 wirklich tot
 *     kalt 159 Drücke   0 leere Aufnahmen
 *                       6× ptt-stiller-strom spitze=0 — und alle sechs kamen
 *                       mit 190 kB bis 5,8 MB an und wurden sauber erkannt
 *
 * Auf dem kalten Weg misst die Wache also manchmal Null, obwohl Ton fließt.
 * Den Unterschied macht die ECHTE Aufnahme: tot heißt, der Recorder hat
 * praktisch nichts geliefert (5 Bytes). Darum gilt eine Leitung erst dann
 * als tot, wenn BEIDE nichts sehen — die Wache und der Recorder.
 *
 * ⚠️ `spurStumm=ja` (track.muted beim Start) taugt NICHT als Vorzeichen:
 *    einmal stand es bei einer toten, einmal bei einer guten Aufnahme.
 */

import { TAKT_STEHT_AB_MS } from "./rechentakt.js";

/**
 * Unter wie vielen Recorder-Bytes gilt eine Aufnahme als leer. (Ticket 2518)
 *
 * Die toten Aufnahmen hatten 5 Bytes, die Fehlalarme der Wache ab 190 kB.
 * Eine einzige Zeitscheibe echten Tons (250 ms bei 64 kbit/s) sind rund
 * 2 kB. 64 Bytes liegen weit unter jedem Ton und weit über den 5 Bytes —
 * die Schwelle muss nichts Feines unterscheiden, nur „nichts“ von „etwas“.
 */
export const LEER_UNTER_BYTES = 64;

/**
 * Wann eine WARME Leitung mitten im Druck geprüft wird. (Ticket 2518)
 *
 * Gemessen ab `recorder.start(250)`: bis dahin liegen vier Zeitscheiben
 * vor, eine tragende Leitung hat also längst Bytes geliefert. Die toten
 * Fälle waren schon bei 471 ms still (`dauer=471ms spitze=0`) — länger
 * zu warten kostet nur Worte, die ins Leere gesprochen werden.
 */
export const WECHSEL_NACH_MS = 1200;

/**
 * Wann mitten im Druck auf eine STUMME QUELLE geprüft wird. (26.09.2026,
 * Ticket 2643)
 *
 * Die Regel braucht den Beweis, dass die Uhr der Wache lief — und den gibt
 * es erst nach TAKT_STEHT_AB_MS gezählter Kontext-Zeit. Gezählt wird erst ab
 * dem zweiten Takt der Wache (100 ms), und ein Takt kann sich verspäten:
 * 600 ms Spielraum über den 1200 ms. Kommt der Beweis trotzdem nicht
 * zustande, entscheidet die Prüfung nichts. Später als die Prüfung der
 * toten warmen Leitung (WECHSEL_NACH_MS), damit die zuerst greift. Was bis
 * hierher „gesprochen“ wurde, war ohnehin nur Stille.
 */
export const STUMM_PRUEFEN_NACH_MS = 1800;

/** Woher die Leitung dieser Aufnahme kam. */
export type LeitungsWeg = "warm" | "kalt" | "gewechselt";

/**
 * Wie lange eine Aufnahme mindestens gelaufen sein muss, bevor ihr
 * Schweigen etwas ueber die Leitung aussagt.
 *
 * Die gemessenen Faelle liefen rund eine Sekunde (4,2 kB bei 64 kbit/s).
 * 400 ms liegen deutlich darunter und deutlich ueber einem Fehlgriff.
 */
export const BEWEIS_AB_MS = 400;

export interface StromBefund {
  /** Hoechster Ausschlag waehrend der Aufnahme, 0…1. */
  spitze: number;
  /** Wie lange gemessen wurde. */
  dauerMs: number;
  /** Was der Recorder wirklich geliefert hat: Summe aller `ev.data.size`. */
  bytes: number;
  /** Nur für die Zeile — entscheidet nichts. */
  weg?: LeitungsWeg;
  /**
   * Zustand des Wache-Kontexts beim ersten Takt, und ob er je lief.
   * ⚠️ Nur Messung (Ticket 2518): ob ein angehaltener Kontext die Nullen
   *    der Fehlalarme erklärt, ist nicht belegt — erst messen.
   */
  kontextErst?: string;
  kontextLief?: boolean;
  /**
   * Lief die Uhr des Wache-Kontexts mit der Wanduhr mit? (26.09.2026, Ticket
   * 2634) `ctx=running` allein beweist nichts: bei 61 von 68 Nullen meldete
   * der Kontext „running" und rechnete nicht. Undefiniert, wenn zu kurz
   * gemessen wurde (rechentakt.ts, TAKT_STEHT_AB_MS).
   */
  taktLief?: boolean;
}

/**
 * Ist die Leitung tot — also: hat sie ueber eine aussagekraeftige Dauer
 * NICHTS geliefert, weder an die Wache noch an den Recorder?
 *
 * ⚠️ Blinder Fleck (Nachprüfung 24.09.2026): Eine Leitung, die digitale
 *    Stille liefert, während der Encoder trotzdem Bytes schreibt (#2494,
 *    17:56: 4278 Bytes bei −72 dBFS), gilt hier NICHT mehr als tot. 22.–24.09.
 *    kam das nicht vor. Das Zeichen dafür im Journal: `ptt-wache-null …
 *    ctxLief=ja` — bei laufendem Kontext zeigt echtes Rauschen immer eine
 *    Spitze über 0. Erst wenn das auftaucht, die Regel nachschärfen.
 *    → 26.09.2026 aufgetaucht (Konto 23, Ticket 2643): istStummeQuelle.
 */
export function istToterStrom(b: StromBefund): boolean {
  if (b.dauerMs < BEWEIS_AB_MS) return false;
  if (b.spitze > 0) return false;
  return b.bytes < LEER_UNTER_BYTES;
}

/**
 * Die Wache sah Null, aber der Recorder hat geliefert: ein Fehlalarm der
 * Wache, keine tote Leitung. Wird gemeldet, damit man ihn zählen kann —
 * und sonst nichts.
 */
function istWachenFehlalarm(b: StromBefund): boolean {
  if (b.dauerMs < BEWEIS_AB_MS || b.spitze > 0) return false;
  return b.bytes >= LEER_UNTER_BYTES;
}

/**
 * Liefert die Quelle digitale Stille, obwohl alles läuft? (26.09.2026,
 * Ticket 2643)
 *
 * Konto 23: acht Sprechtasten-Aufnahmen seit 24.09., alle mit
 * `max_volume -91 dB` — exakt Null, bis 42 s lang. Die Zeile dazu:
 * `ptt-wache-null dauer=42823ms weg=kalt bytes=154548 ctx=running
 * ctxLief=ja takt=lief`. Der Encoder schrieb Bytes, die Uhr der Wache lief
 * nachweislich — und die Quelle gab Nullen.
 *
 * ⚠️ Das Gegenbild ist Konto 1 (Ticket 2634): dieselbe Wache-Null, aber
 *    `takt=steht` — dort rechnete die Tonverarbeitung der Seite nicht, die
 *    Aufnahme selbst war heil. Ohne den Beweis einer laufenden Uhr
 *    (`taktLief === true`) sagt eine Null darum nichts.
 *
 * ⚠️ Exakt Null, nicht „sehr leise“: die Wache misst in Gleitkomma, dort
 *    zeigt jedes echte Mikrofon Rauschen über Null. (In 8 Bit las leises
 *    Raumrauschen als 0 — ein stiller Raum wäre eine stumme Quelle gewesen.)
 */
export function istStummeQuelle(b: StromBefund): boolean {
  if (b.dauerMs < TAKT_STEHT_AB_MS) return false;
  return b.spitze === 0 && b.taktLief === true;
}

/** Weg, Bytes und Kontext — was jede Zeile mitschreibt. */
function messTeil(b: StromBefund): string {
  const kontext =
    b.kontextErst === undefined
      ? ""
      : ` ctx=${b.kontextErst} ctxLief=${b.kontextLief ? "ja" : "nein"}`;
  const takt = b.taktLief === undefined ? "" : ` takt=${b.taktLief ? "lief" : "steht"}`;
  return `${b.weg ? ` weg=${b.weg}` : ""} bytes=${b.bytes}${kontext}${takt}`;
}

/** Dauer, Weg, Bytes und Kontext — der Befund als Teil einer Zeile. */
export function befundTeil(b: StromBefund): string {
  return `dauer=${Math.round(b.dauerMs)}ms${messTeil(b)}`;
}

/**
 * Was im Protokoll stehen soll. Gibt null zurueck, wenn nichts zu melden
 * ist — eine gesunde Leitung ist kein Ereignis.
 *
 * Ticket 2643: die stumme Quelle vor der Wache-Null — sonst stünde sie
 * weiter unter dem Namen eines Fehlalarms. Die tote Leitung bleibt vorn:
 * ohne Bytes ist sie die ältere, schärfere Aussage.
 */
export function stromMeldung(b: StromBefund): string | null {
  const dauer = `dauer=${Math.round(b.dauerMs)}ms`;
  if (istToterStrom(b)) return `ptt-stiller-strom ${dauer} spitze=0${messTeil(b)}`;
  if (istStummeQuelle(b)) return `ptt-stumme-quelle ${befundTeil(b)}`;
  if (istWachenFehlalarm(b)) return `ptt-wache-null ${befundTeil(b)}`;
  return null;
}

/** Was die Prüfung mitten im Druck sieht. */
export interface LeitungImDruck extends StromBefund {
  /** Läuft der Recorder noch? Nach dem Loslassen ist nichts mehr zu retten. */
  recorderLaeuft: boolean;
}

/**
 * Soll die warme Leitung mitten im Druck gegen eine frische getauscht
 * werden? (24.09.2026, Ticket 2518)
 *
 * Heute 12:03: App nach 1,6 h aus dem Hintergrund, 1,4 s später ein Druck
 * auf die warme Leitung — 38 Sekunden gesprochen, `spitze=0`, 5 Bytes. Der
 * nächste Druck (kalt) lief sauber. Die Regel darüber gab die tote Leitung
 * erst NACH dem Druck frei; die erste Aufnahme war verloren.
 *
 * ⚠️ Nur der warme Weg. Auf dem kalten Weg kam 22.–24.09. keine einzige
 *    leere Aufnahme vor — und dort hat die Wache sechs Fehlalarme. Und nur
 *    „warm“, nicht „gewechselt“: ein Druck wechselt höchstens einmal.
 */
export function sollLeitungWechseln(l: LeitungImDruck): boolean {
  if (l.weg !== "warm" || !l.recorderLaeuft) return false;
  return istToterStrom(l);
}

/**
 * Was mitten im Druck mit einer stummen Quelle geschieht. (Ticket 2643)
 *
 * Auf dem warmen und dem kalten Weg wird die Leitung getauscht. Nach einem
 * Tausch („gewechselt“) wird nicht noch einmal getauscht — dann bleibt nur,
 * es dem Menschen zu sagen.
 */
export function stummImDruck(l: LeitungImDruck): "tauschen" | "sagen" | null {
  if (!l.recorderLaeuft || !istStummeQuelle(l)) return null;
  if (l.weg === "warm" || l.weg === "kalt") return "tauschen";
  return l.weg === "gewechselt" ? "sagen" : null;
}

/** Was der Wechsel über die verworfene Leitung weiß. */
export interface VerworfeneLeitung {
  /** Wie oft der Recorder bis zur Prüfung geliefert hat. */
  teile: number;
  /** Bytes bis zur Prüfung. */
  bytes: number;
  /** Der Schlussblock beim Stopp — null, wenn er nicht rechtzeitig kam. */
  rest: number | null;
  kontextErst: string;
  kontextLief: boolean;
  /**
   * Ticket 2643: verworfen, weil die Quelle stumm war. Der Encoder schreibt
   * auch Stille in Bytes — ein Schlussblock beweist dann keinen Fehlalarm.
   */
  stumm?: boolean;
}

/**
 * Der Messteil jeder Wechselzeile. (Nachprüfung 24.09.2026, Ticket 2518)
 *
 * Gewechselt wird eine GESUNDE warme Leitung nur, wenn zweierlei zusammen-
 * trifft: die Wache ist blind (Kontext angehalten, liest Nullen) UND iOS
 * liefert die erste Zeitscheibe erst nach WECHSEL_NACH_MS. Dann steckt der
 * Satzanfang im Schlussblock des verworfenen Recorders. Ab LEER_UNTER_BYTES
 * war dort Ton — `fehlalarm=ja` steht dann sofort im Journal, bevor jemand
 * am Gerät Worte vermisst. Entschieden wird darauf nichts.
 */
export function verworfenMessung(v: VerworfeneLeitung): string {
  const fehlalarm = !v.stumm && v.rest !== null && v.rest >= LEER_UNTER_BYTES;
  const befund = messTeil({ spitze: 0, dauerMs: 0, ...v });
  return `teile=${v.teile} rest=${v.rest ?? "?"}${befund}${fehlalarm ? " fehlalarm=ja" : ""}`;
}
